import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface IUseConversationComposer {
  isConnected: boolean;
  isVoiceMode: boolean;
  sendMessage: (message: string) => boolean;
  sendUserActivity: () => void;
}

const useConversationComposer = ({
  isConnected,
  isVoiceMode,
  sendMessage,
  sendUserActivity,
}: IUseConversationComposer) => {
  const [textInput, setTextInput] = useState('');
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const voiceStageRef = useRef<HTMLDivElement | null>(null);

  const sendTextMessage = useCallback(() => {
    if (isVoiceMode) {
      return;
    }

    if (sendMessage(textInput)) {
      setTextInput('');
    }
  }, [isVoiceMode, sendMessage, textInput]);

  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault();
    sendTextMessage();
  }, [sendTextMessage]);

  const handleInputKeyDown = useCallback((
    event: KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendTextMessage();
    }
  }, [sendTextMessage]);

  const handleTextInputChange = useCallback((value: string) => {
    setTextInput(value);

    if (isConnected) {
      sendUserActivity();
    }
  }, [isConnected, sendUserActivity]);

  useEffect(() => {
    let focusFrame: number | null = null;

    if (isConnected) {
      focusFrame = window.requestAnimationFrame(() => {
        if (isVoiceMode) {
          voiceStageRef.current?.focus();
        } else {
          textInputRef.current?.focus();
        }
      });
    }

    return () => {
      if (focusFrame !== null) {
        window.cancelAnimationFrame(focusFrame);
      }
    };
  }, [isConnected, isVoiceMode]);

  return {
    handleInputKeyDown,
    handleSubmit,
    handleTextInputChange,
    textInput,
    textInputRef,
    voiceStageRef,
  };
};

export default useConversationComposer;
